import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Home, FileText, ShoppingCart, MapPin, LogIn, Gift, ChefHat, Heart, ShoppingBag, Sparkles, ArrowRight, CreditCard, Search, UtensilsCrossed } from "lucide-react";
import { useAppSettings, resolveImageUrl } from "../context/AppSettingsContext";
import PoweredBy from "../components/PoweredBy";
import FavoriteStore from "../components/FavoriteStore";
import { PublicShell, NewsCard, useBrand } from "./PublicHome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tq = () => { const t = new URLSearchParams(window.location.search).get("t") || localStorage.getItem("kl_tenant"); return t ? { headers: { "X-Tenant": t } } : {}; };
const ICONS = { home: Home, volantino: FileText, shop: ShoppingCart, negozi: MapPin, "punti-vendita": MapPin, accedi: LogIn, raccolta: Gift, premi: Gift, ricette: ChefHat, iniziative: Heart, prodotti: ShoppingBag, bistro: UtensilsCrossed };
const iconOf = (k) => ICONS[k] || Sparkles;
const card = { borderRadius: "var(--tk-radius-card, 24px)", boxShadow: "var(--tk-shadowCard, 0 6px 18px rgba(0,0,0,0.07))" };
const btn = { borderRadius: "var(--tk-radius-button, 14px)" };

export function HomeTile({ t, brand }) {
  const Icon = iconOf(t.key);
  const img = resolveImageUrl(t.image_url);
  const base = "relative block overflow-hidden p-4 min-h-[150px] flex flex-col justify-end transition active:scale-[0.98] hover:-translate-y-0.5";
  if (t.style === "image" && img) {
    return (
      <Link to={t.to || "/home"} className={`${base} bg-white`} style={card} data-testid={`ch-tile-${t.key}`}>
        <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.72))" }} />
        <div className="relative text-white"><div className="font-heading font-extrabold text-lg leading-tight">{t.title}</div>{t.subtitle && <div className="text-xs opacity-90 mt-0.5">{t.subtitle}</div>}</div>
      </Link>
    );
  }
  if (t.style === "solid") {
    return (
      <Link to={t.to || "/home"} className={`${base} text-white`} style={{ ...card, background: brand }} data-testid={`ch-tile-${t.key}`}>
        <Icon className="h-12 w-12 opacity-95 mb-auto" strokeWidth={1.6} />
        <div className="font-heading font-extrabold text-lg leading-tight">{t.title}</div>{t.subtitle && <div className="text-xs opacity-90 mt-0.5">{t.subtitle}</div>}
      </Link>
    );
  }
  return (
    <Link to={t.to || "/home"} className={base} style={{ ...card, background: "var(--tk-surfaceAlt, #F4F4F5)", color: "var(--tk-text, #18181B)" }} data-testid={`ch-tile-${t.key}`}>
      <span className="h-12 w-12 rounded-full flex items-center justify-center text-white mb-auto shadow-sm" style={{ background: brand }}>{img ? <img src={img} alt="" className="h-7 w-7 object-contain" /> : <Icon className="h-6 w-6" />}</span>
      <div className="font-heading font-extrabold text-lg leading-tight mt-3">{t.title}</div>{t.subtitle && <div className="text-xs mt-0.5" style={{ color: "var(--tk-textMuted, #71717a)" }}>{t.subtitle}</div>}
    </Link>
  );
}

function Hero({ h, brand }) {
  if (!h || (!h.title && !h.image_url)) return null;
  const img = resolveImageUrl(h.image_url);
  return (
    <section className="relative overflow-hidden min-h-[210px] flex flex-col justify-end p-5 text-white" style={{ ...card, background: brand }} data-testid="ch-hero">
      {img && <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" />}
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.7))" }} />
      <div className="relative space-y-2">
        {h.eyebrow && <div className="text-[11px] font-extrabold tracking-[0.2em] uppercase" data-testid="ch-hero-eyebrow">{h.eyebrow}</div>}
        <h2 className="font-heading font-extrabold text-2xl leading-tight max-w-[80%] text-white" style={{ color: "#fff" }} data-testid="ch-hero-title">{h.title}</h2>
        {h.cta && <Link to={h.to || "/home"} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white shadow" style={{ ...btn, background: brand }} data-testid="ch-hero-cta">{h.cta} <ArrowRight className="h-4 w-4" /></Link>}
      </div>
    </section>
  );
}

function Loyalty({ l, brand, cardName }) {
  if (!l || l.enabled === false) return null;
  const logo = resolveImageUrl(l.logo_url);
  return (
    <section className="bg-white p-4 space-y-3" style={card} data-testid="ch-loyalty">
      <div className="flex items-center gap-3">
        {logo ? <img src={logo} alt={cardName} className="h-12 max-w-[120px] object-contain" /> : <span className="font-heading font-extrabold text-xl" style={{ color: brand }}>{cardName}</span>}
        <div className="min-w-0"><div className="text-[11px] font-extrabold tracking-[0.18em] uppercase" style={{ color: brand }}>{l.badge}</div><p className="text-xs leading-snug" style={{ color: "var(--tk-textMuted, #52525b)" }}>{l.text}</p></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Link to="/cliente/accedi" className="flex items-center justify-center gap-2 py-3 text-sm font-bold text-white" style={{ ...btn, background: brand }} data-testid="ch-loyalty-login"><ArrowRight className="h-4 w-4" /> {l.login_label || "Accedi"}</Link>
        <Link to="/richiedi-card" className="flex items-center justify-center gap-2 py-3 text-sm font-bold text-white" style={{ ...btn, background: brand }} data-testid="ch-loyalty-request"><CreditCard className="h-4 w-4" /> {l.request_label || "Richiedi card"}</Link>
      </div>
    </section>
  );
}

export default function ChannelHome() {
  const { settings, brand } = useBrand();
  const { settings: app } = useAppSettings();
  const navigate = useNavigate();
  const cfg = app.home_layout || {};
  const [news, setNews] = useState([]);
  const [branch, setBranch] = useState(localStorage.getItem("kl_pub_branch") || "");
  useEffect(() => { axios.get(`${API}/public/news?limit=3${branch ? `&branch_id=${encodeURIComponent(branch)}` : ""}`, tq()).then(({ data }) => setNews(data || [])).catch(() => {}); }, [branch]);
  const go = (to) => { if (to?.startsWith("/scopri")) { navigate(to); return; } navigate(to || "/home"); };
  const nav = (cfg.bottom_nav || []).map((n) => ({ key: n.key, label: n.label, icon: iconOf(n.key), active: n.key === "home", onClick: () => go(n.to) }));
  const tiles = (cfg.tiles || []).filter((t) => !t.hidden);
  return (
    <PublicShell testid="channel-home" bottomNav={nav.length ? nav : undefined} headerRight={<Link to="/home/punti-vendita" aria-label="Cerca punto vendita" className="h-11 w-11 rounded-2xl bg-white shadow-sm ring-1 ring-zinc-200 flex items-center justify-center" style={{ color: brand }} data-testid="ch-search"><Search className="h-5 w-5" /></Link>}>
      <div className="-mt-1" data-testid="ch-welcome">
        {settings.payoff && <div className="text-xs italic" style={{ color: "var(--tk-textMuted, #71717a)" }}>{settings.payoff}</div>}
        <h1 className="font-heading font-extrabold text-2xl leading-tight mt-1" style={{ color: "var(--tk-ink, #111)" }}>{cfg.welcome_title || `Benvenuto in ${settings.app_name || ""}`}</h1>
        {cfg.welcome_subtitle && <p className="text-sm mt-0.5" style={{ color: "var(--tk-textMuted, #52525b)" }}>{cfg.welcome_subtitle}</p>}
      </div>
      <Hero h={cfg.hero} brand={brand} />
      <Loyalty l={cfg.loyalty} brand={brand} cardName={settings.card_name || "Carta fedeltà"} />
      {cfg.favorite_store?.enabled !== false && <FavoriteStore brand={brand} onChange={setBranch} />}
      <section className="grid grid-cols-2 gap-3 !mt-4" data-testid="ch-tiles">{tiles.map((t) => <HomeTile key={t.key} t={t} brand={brand} />)}</section>
      {cfg.news?.enabled !== false && (
        <section className="space-y-3" data-testid="ch-news">
          <div className="flex items-end justify-between"><h2 className="font-heading font-extrabold text-xl" style={{ color: "var(--tk-ink, #111)" }}>{cfg.news?.title || "News ed eventi"}</h2><Link to="/home/news" className="text-sm font-semibold" style={{ color: brand }} data-testid="ch-news-more">{cfg.news?.more || "Vedi tutte"}</Link></div>
          {news.length === 0 ? <p className="text-sm" style={{ color: "var(--tk-textMuted, #71717a)" }} data-testid="ch-news-empty">Nessuna news pubblicata al momento.</p> : news.map((n) => <NewsCard key={n.id} n={n} brand={brand} />)}
        </section>
      )}
      <div className="flex justify-center pt-2"><PoweredBy testid="ch-powered-by" /></div>
    </PublicShell>
  );
}
